import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Users,
  Sparkles,
  AlarmClock,
  MessageCircle,
  Globe2,
  Check,
  ArrowRight,
  ChevronDown,
  CalendarCheck,
  FileSignature,
  RefreshCw,
  Wallet,
  Star,
  Quote,
} from 'lucide-react';
import InstallPrompt from './InstallPrompt';
import { resolveApiUrl } from '../services/api';

interface LandingProps {
  onSignIn: () => void;
  onSignUp: () => void;
}

// Healthcare-soft, calming palette. Forced light regardless of app theme,
// because a marketing page for clinics should feel airy and welcoming.
const C = {
  bg: '#f7f4ee',          // Sereno warm off-white
  bgAlt: '#efeae0',       // Sereno sand band
  surface: '#ffffff',
  ink: '#1a2420',         // Sereno deep forest ink
  inkMuted: '#5a6660',
  inkFaint: '#98a39d',
  line: '#e5ded1',
  lineSoft: '#efeae0',
  accent: '#0b3b2e',      // Sereno deep forest green
  accentHover: '#0f4a3a',
  accentSoft: 'rgba(11, 59, 46, 0.08)',
  accentContrast: '#f5efe2',
  sand: '#d4b896',
  shadow: '0 1px 3px rgba(11, 59, 46, 0.04), 0 12px 32px rgba(11, 59, 46, 0.06)',
  shadowSm: '0 1px 2px rgba(11, 59, 46, 0.04)',
};

const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'fr', label: 'Français' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
];

interface PublicPlan {
  id: string;
  name: string;
  price: string;
  pricePeriod: string;
  monthlyPrice?: string;
  yearlyPrice?: string;
  featuresList: string[];
  includedSeats?: number;
  seatMonthlyPrice?: string;
}

const Landing: React.FC<LandingProps> = ({ onSignIn, onSignUp }) => {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || 'en').slice(0, 2);
  const [plans, setPlans] = useState<{ free?: PublicPlan; pro?: PublicPlan; clinic?: PublicPlan }>({});

  // For first-time visitors with no stored preference, detect language by country (IP geolocation).
  useEffect(() => {
    const stored = localStorage.getItem('nice-agenda-language');
    if (stored) return;

    const COUNTRY_LANG: Record<string, string> = {
      BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', GW: 'pt', ST: 'pt', TL: 'pt',
      ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', VE: 'es', CL: 'es', EC: 'es',
      GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es',
      CR: 'es', PA: 'es', UY: 'es', GQ: 'es',
      DE: 'de', AT: 'de', LI: 'de', CH: 'de',
      IT: 'it', SM: 'it', VA: 'it',
      FR: 'fr', MC: 'fr', BE: 'fr', LU: 'fr',
      JP: 'ja',
      KR: 'ko',
      CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh', SG: 'zh',
    };

    const applyLang = (countryCode: string | undefined) => {
      const lang = (countryCode && COUNTRY_LANG[countryCode]) || 'en';
      i18n.changeLanguage(lang);
      localStorage.setItem('nice-agenda-language', lang);
    };

    fetch('https://api.country.is/')
      .then(r => r.ok ? r.json() : null)
      .then(data => applyLang(data?.country))
      .catch(() => applyLang(undefined));
  }, [i18n]);

  useEffect(() => {
    const currency = currentLang === 'pt' ? 'BRL' : 'USD';
    fetch(resolveApiUrl(`/api/system-config/public-plans?currency=${currency}`))
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.success && j.data) setPlans(j.data); })
      .catch(() => {});
  }, [currentLang]);

  // ── Dynamic SEO: title, meta description, OG tags, html lang, FAQPage JSON-LD ──
  useEffect(() => {
    type LangEntry = [string, string]; // [title, description]
    const SEO: Record<string, LangEntry> = {
      en: ['Nice — Smart Scheduling & Patient Management for Clinics', 'Online self-booking, WhatsApp confirmations that kill no-shows, digital consent forms, and automatic recall for aesthetic and medical clinics. Free to start.'],
      pt: ['Nice — Agenda Inteligente e Gestão de Pacientes para Clínicas', 'Agendamento online, confirmações por WhatsApp que eliminam faltas, termos de consentimento digitais e recall automático para clínicas. Grátis para começar.'],
      es: ['Nice — Agenda Inteligente para Clínicas', 'Reservas online, confirmaciones por WhatsApp, consentimientos digitales y recaptación automática para clínicas estéticas y médicas. Empieza gratis.'],
      de: ['Nice — Terminverwaltung für Kliniken', 'Online-Buchung, WhatsApp-Bestätigungen, digitale Einwilligungsformulare und automatische Rückrufaktionen für Kliniken. Kostenlos starten.'],
      it: ['Nice — Gestione Appuntamenti per Cliniche', 'Prenotazione online, conferme WhatsApp, moduli di consenso digitali e richiamo automatico per cliniche estetiche e mediche. Inizia gratis.'],
      fr: ['Nice — Agenda Intelligent pour Cliniques', 'Réservation en ligne, confirmations WhatsApp, formulaires de consentement numériques et rappel automatique pour cliniques. Démarrez gratuitement.'],
      ja: ['Nice — クリニック向けスマートスケジュール管理', 'オンライン予約・WhatsApp確認・デジタル同意書・自動リコールでクリニックの経営をサポート。無料で始められます。'],
      ko: ['Nice — 클리닉을 위한 스마트 예약 관리', '온라인 예약, WhatsApp 확인, 디지털 동의서, 자동 리콜로 클리닉 운영을 지원합니다. 무료로 시작하세요.'],
      zh: ['Nice — 诊所智能预约管理系统', '在线预约、WhatsApp确认、数字知情同意书和自动随访，为诊所提供一站式解决方案。免费开始。'],
    };
    const [title, description] = SEO[currentLang] ?? SEO['en'];

    document.title = title;
    document.documentElement.lang = currentLang === 'pt' ? 'pt-BR' : currentLang;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // FAQPage JSON-LD — language-aware, using live i18next translations
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [1, 2, 3, 4, 5, 6].map(n => ({
        '@type': 'Question',
        name: t(`landing.faq.q${n}.question`),
        acceptedAnswer: { '@type': 'Answer', text: t(`landing.faq.q${n}.answer`) },
      })),
    };
    let faqScript = document.getElementById('nice-faq-jsonld') as HTMLScriptElement | null;
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.id = 'nice-faq-jsonld';
      faqScript.type = 'application/ld+json';
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      document.title = 'Nice';
      const faqSc = document.getElementById('nice-faq-jsonld');
      if (faqSc) faqSc.remove();
    };
  }, [currentLang, t]);

  const onChangeLang = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('nice-agenda-language', code);
  };

  const features = [
    { icon: Calendar, key: 'calendar' },
    { icon: Users, key: 'clients' },
    { icon: Sparkles, key: 'briefing' },
    { icon: AlarmClock, key: 'reminders' },
    { icon: MessageCircle, key: 'whatsapp' },
    { icon: Globe2, key: 'multilang' },
  ];

  return (
    <div
      role="main"
      style={{
        position: 'absolute',
        inset: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        background: C.bg,
        color: C.ink,
        WebkitUserSelect: 'text',
        userSelect: 'text',
        // Soft warm gradient wash, very subtle
        backgroundImage: `radial-gradient(1200px 600px at 50% -200px, ${C.accentSoft}, transparent 60%)`,
      }}
    >
      {/* Top nav */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(251, 250, 246, 0.85)',
          backdropFilter: 'blur(12px) saturate(140%)',
          WebkitBackdropFilter: 'blur(12px) saturate(140%)',
          borderBottom: `1px solid ${C.lineSoft}`,
        }}
      >
        <nav style={navInner} className="landing-nav-inner" aria-label="Main navigation">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <BrandMark />
            <span
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.4px',
                color: C.ink,
              }}
            >
              Nice
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LanguageSelect value={currentLang} onChange={onChangeLang} />
            <button onClick={onSignIn} style={btnGhost} className="landing-nav-signin">
              {t('landing.nav.signIn')}
            </button>
            <button onClick={onSignUp} style={btnPrimary} className="landing-nav-signup">
              {t('landing.nav.signUp')}
            </button>
          </div>
        </nav>
      </header>

      {/* Hero — split layout with real clinic photo */}
      <section style={{ ...section, paddingTop: 80, paddingBottom: 64 }}>
        <div
          style={{
            maxWidth: 1180,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 56,
            alignItems: 'center',
          }}
        >
          <div>
            <span style={tag}>{t('landing.hero.tagline')}</span>
            <h1
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(36px, 5vw, 56px)',
                fontWeight: 500,
                lineHeight: 1.05,
                letterSpacing: '-1.3px',
                margin: '20px 0 18px',
                color: C.ink,
              }}
            >
              {t('landing.hero.headline')}
            </h1>
            <p style={{ fontSize: 17.5, lineHeight: 1.6, color: C.inkMuted, marginBottom: 32 }}>
              {t('landing.hero.subheadline')}
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={onSignUp} style={{ ...btnPrimary, padding: '14px 24px', fontSize: 15 }}>
                {t('landing.hero.cta.primary')}
                <ArrowRight size={16} style={{ marginLeft: 8 }} />
              </button>
              <button onClick={onSignIn} style={{ ...btnGhost, padding: '14px 24px', fontSize: 15 }}>
                {t('landing.hero.cta.secondary')}
              </button>
            </div>
            <p style={{ marginTop: 18, fontSize: 13, color: C.inkFaint }}>
              {t('landing.hero.disclaimer')}
            </p>
          </div>

          <HeroVisual />
        </div>
      </section>

      {/* Pillars — the commercial differentiators */}
      <Pillars onSignUp={onSignUp} />

      {/* How it works */}
      <HowItWorks />

      {/* Features */}
      <section style={{ ...section, paddingTop: 64, paddingBottom: 88 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={sectionTitle}>{t('landing.features.title')}</h2>
          <p style={sectionSubtitle}>{t('landing.features.subtitle')}</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 18,
              marginTop: 48,
            }}
          >
            {features.map(({ icon: Icon, key }) => (
              <div key={key} style={featureCard}>
                <div style={featureIcon}>
                  <Icon size={20} />
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 19,
                    fontWeight: 600,
                    margin: '14px 0 8px',
                    color: C.ink,
                    letterSpacing: '-0.3px',
                  }}
                >
                  {t(`landing.features.${key}.title`)}
                </h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.6, color: C.inkMuted }}>
                  {t(`landing.features.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section style={{ ...section, paddingTop: 24, paddingBottom: 24 }}>
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            justifyContent: 'center',
            alignItems: 'center',
            color: C.inkFaint,
            fontSize: 13,
          }}
        >
          <TrustBadge label={t('landing.trust.privacy')} />
          <TrustBadge label={t('landing.trust.lgpd')} />
          <TrustBadge label={t('landing.trust.uptime')} />
          <TrustBadge label={t('landing.trust.support')} />
        </div>
      </section>

      {/* Social proof — stats + testimonials */}
      <SocialProof />

      {/* Banner with real clinic photo */}
      <ClinicBanner />

      {/* Financial teaser — coming next */}
      <FinancialTeaser />

      {/* Pricing */}
      <section style={{ ...section, background: C.bgAlt, paddingTop: 88, paddingBottom: 88 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={sectionTitle}>{t('landing.pricing.title')}</h2>
          <p style={sectionSubtitle}>{t('landing.pricing.subtitle')}</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 20,
              marginTop: 48,
            }}
          >
            <PricingTier
              name={t('landing.pricing.starter.name')}
              price={plans.free?.price || t('landing.pricing.starter.price')}
              period={t('landing.pricing.starter.period')}
              description={t('landing.pricing.starter.description')}
              features={[
                t('landing.pricing.starter.f1'),
                t('landing.pricing.starter.f2'),
                t('landing.pricing.starter.f3'),
                t('landing.pricing.starter.f4'),
              ]}
              ctaLabel={t('landing.pricing.starter.cta')}
              onCta={onSignUp}
            />
            <PricingTier
              highlighted
              badge={t('landing.pricing.pro.badge')}
              name={t('landing.pricing.pro.name')}
              price={plans.pro?.price || t('landing.pricing.pro.price')}
              period={t('landing.pricing.pro.period')}
              description={t('landing.pricing.pro.description')}
              features={[
                t('landing.pricing.pro.f1'),
                t('landing.pricing.pro.f2'),
                t('landing.pricing.pro.f3'),
                t('landing.pricing.pro.f4'),
                t('landing.pricing.pro.f5'),
                t('landing.pricing.pro.f6'),
              ]}
              ctaLabel={t('landing.pricing.pro.cta')}
              onCta={onSignUp}
            />
            <PricingTier
              name={t('landing.pricing.clinic.name')}
              price={plans.clinic?.price || t('landing.pricing.clinic.price')}
              period={t('landing.pricing.clinic.period')}
              description={t('landing.pricing.clinic.description')}
              note={
                plans.clinic?.includedSeats && plans.clinic?.seatMonthlyPrice
                  ? t('landing.pricing.clinic.seatNote', {
                      seats: plans.clinic.includedSeats,
                      price: plans.clinic.seatMonthlyPrice,
                      defaultValue: `Inclui ${plans.clinic.includedSeats} profissionais · +${plans.clinic.seatMonthlyPrice}/mês por adicional`,
                    })
                  : t('landing.pricing.clinic.seatNoteFallback', {
                      defaultValue: 'Inclui 6 profissionais · +R$ 30/mês por adicional',
                    })
              }
              features={[
                t('landing.pricing.clinic.f1'),
                t('landing.pricing.clinic.f2'),
                t('landing.pricing.clinic.f3'),
                t('landing.pricing.clinic.f4'),
                t('landing.pricing.clinic.f5'),
              ]}
              ctaLabel={t('landing.pricing.clinic.cta')}
              onCta={onSignUp}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <Faq />

      {/* Final CTA */}
      <section style={{ ...section, paddingTop: 88, paddingBottom: 88 }}>
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            textAlign: 'center',
            background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentHover} 100%)`,
            color: C.accentContrast,
            borderRadius: 20,
            padding: '56px 36px',
            boxShadow: C.shadow,
          }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(28px, 4vw, 38px)',
              fontWeight: 500,
              letterSpacing: '-0.7px',
              marginBottom: 14,
            }}
          >
            {t('landing.finalCta.title')}
          </h2>
          <p style={{ opacity: 0.88, fontSize: 16, marginBottom: 28, lineHeight: 1.55 }}>
            {t('landing.finalCta.subtitle')}
          </p>
          <button
            onClick={onSignUp}
            style={{
              padding: '14px 28px',
              background: C.accentContrast,
              color: C.accent,
              border: 'none',
              borderRadius: 12,
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {t('landing.finalCta.button')}
            <ArrowRight size={16} style={{ marginLeft: 8 }} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          ...section,
          paddingTop: 32,
          paddingBottom: 36,
          borderTop: `1px solid ${C.lineSoft}`,
          color: C.inkFaint,
          fontSize: 13,
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BrandMark size={28} />
            <span>© {new Date().getFullYear()} Nice</span>
          </div>
          <span style={{ color: C.inkFaint }}>{t('landing.footer.tagline')}</span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="/terms-and-conditions" style={{ color: C.inkFaint, textDecoration: 'none' }}>Terms</a>
            <a href="/privacy" style={{ color: C.inkFaint, textDecoration: 'none' }}>Privacy</a>
            <a href="/refund" style={{ color: C.inkFaint, textDecoration: 'none' }}>Refund</a>
          </div>
        </div>
      </footer>
      <InstallPrompt />
    </div>
  );
};

// ── Subcomponents ───────────────────────────────────────────────

const BrandMark: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size / 4,
      background: C.accent,
      color: C.accentContrast,
      display: 'grid',
      placeItems: 'center',
      fontFamily: 'var(--font-serif)',
      fontStyle: 'italic',
      fontSize: size * 0.55,
      fontWeight: 600,
      letterSpacing: '-0.5px',
      flexShrink: 0,
    }}
  >
    N
  </div>
);

const LanguageSelect: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <Globe2 size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.inkMuted, pointerEvents: 'none' }} />
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        appearance: 'none',
        WebkitAppearance: 'none',
        background: 'transparent',
        color: C.ink,
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        padding: '8px 30px 8px 30px',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {LANGUAGES.map(l => (
        <option key={l.code} value={l.code}>
          {l.label}
        </option>
      ))}
    </select>
    <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: C.inkMuted, pointerEvents: 'none' }} />
  </div>
);

const TrustBadge: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <Check size={14} style={{ color: C.accent }} />
    <span>{label}</span>
  </div>
);

// Hot-linked Unsplash photos — free for commercial use.
// Hero: woman receiving a facial spa treatment — aesthetic, no needles.
const HERO_IMG =
  'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=1200&q=80';
// Banner: bright clinical office — modern aesthetic clinic environment.
const BANNER_IMG =
  'https://images.unsplash.com/photo-1758691462863-9e1b8a863140?auto=format&fit=crop&w=1600&q=80';

const HeroVisual: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'relative', minHeight: 380 }}>
      {/* Real photo */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '4 / 5',
          maxHeight: 560,
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: C.shadow,
          background: `linear-gradient(135deg, ${C.accentSoft}, ${C.bgAlt})`,
        }}
      >
        <img
          src={HERO_IMG}
          alt={t('landing.hero.imgAlt')}
          loading="eager"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {/* Soft warm overlay so the photo blends into the page */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, transparent 60%, rgba(45, 110, 88, 0.18) 100%)`,
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Floating agenda preview overlapping bottom-left of the photo */}
      <div
        style={{
          position: 'absolute',
          left: -16,
          bottom: -28,
          width: 'min(320px, 92%)',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          padding: 16,
          boxShadow: C.shadow,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={14} style={{ color: C.accent }} />
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: 14, fontWeight: 600, color: C.ink }}>
              {t('landing.preview.today')}
            </span>
          </div>
          <span style={{ fontSize: 11, color: C.inkFaint }}>3 {t('landing.preview.appointments')}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { time: '09:00', name: 'Maria Silva', proc: t('landing.preview.consultation'), color: C.accent },
            { time: '10:30', name: 'João Costa', proc: t('landing.preview.followup'), color: C.sand },
            { time: '14:00', name: 'Ana Pereira', proc: t('landing.preview.procedure'), color: '#7a9e8b' },
          ].map((appt, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                background: C.bg,
                borderRadius: 9,
              }}
            >
              <div style={{ width: 3, height: 24, borderRadius: 2, background: appt.color }} />
              <div style={{ minWidth: 42, fontSize: 11.5, fontWeight: 600, color: C.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
                {appt.time}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {appt.name}
                </div>
                <div style={{ fontSize: 11, color: C.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {appt.proc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ClinicBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section style={{ ...section, paddingTop: 32, paddingBottom: 32 }}>
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          position: 'relative',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: C.shadow,
          aspectRatio: '21 / 9',
          maxHeight: 360,
        }}
      >
        <img
          src={BANNER_IMG}
          alt={t('landing.banner.imgAlt')}
          loading="lazy"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(90deg, rgba(26, 42, 38, 0.78) 0%, rgba(26, 42, 38, 0.45) 55%, transparent 100%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '32px clamp(24px, 5vw, 64px)',
          }}
        >
          <div style={{ maxWidth: 460, color: '#fbfaf6' }}>
            <h3
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(24px, 3.5vw, 34px)',
                fontWeight: 500,
                letterSpacing: '-0.6px',
                lineHeight: 1.15,
                marginBottom: 12,
              }}
            >
              {t('landing.banner.title')}
            </h3>
            <p style={{ fontSize: 15, opacity: 0.92, lineHeight: 1.55 }}>
              {t('landing.banner.subtitle')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Pillars: the commercial differentiators ─────────────────────
const Pillars: React.FC<{ onSignUp: () => void }> = ({ onSignUp }) => {
  const { t } = useTranslation();
  const pillars = [
    { icon: CalendarCheck, key: 'booking' },
    { icon: MessageCircle, key: 'noshow' },
    { icon: FileSignature, key: 'consent' },
    { icon: RefreshCw, key: 'recall' },
  ];
  return (
    <section style={{ ...section, paddingTop: 56, paddingBottom: 72 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <span style={{ ...tag, display: 'block', width: 'fit-content', margin: '0 auto 16px' }}>
          {t('landing.pillars.tagline')}
        </span>
        <h2 style={sectionTitle}>{t('landing.pillars.title')}</h2>
        <p style={sectionSubtitle}>{t('landing.pillars.subtitle')}</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 20,
            marginTop: 48,
          }}
        >
          {pillars.map(({ icon: Icon, key }) => (
            <div key={key} style={{ ...featureCard, display: 'flex', flexDirection: 'column' }}>
              <div style={{ ...featureIcon, width: 48, height: 48 }}>
                <Icon size={22} />
              </div>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 16,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: C.accent,
                }}
              >
                {t(`landing.pillars.${key}.kicker`)}
              </span>
              <h3
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 20,
                  fontWeight: 600,
                  margin: '6px 0 8px',
                  color: C.ink,
                  letterSpacing: '-0.3px',
                }}
              >
                {t(`landing.pillars.${key}.title`)}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: C.inkMuted, flex: 1 }}>
                {t(`landing.pillars.${key}.description`)}
              </p>
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: C.accent }}>
                <Check size={15} />
                {t(`landing.pillars.${key}.proof`)}
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button onClick={onSignUp} style={{ ...btnPrimary, padding: '14px 26px', fontSize: 15 }}>
            {t('landing.pillars.cta')}
            <ArrowRight size={16} style={{ marginLeft: 8 }} />
          </button>
        </div>
      </div>
    </section>
  );
};

// ── How it works: 3 steps ───────────────────────────────────────
const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  const steps = ['1', '2', '3'];
  return (
    <section style={{ ...section, background: C.bgAlt, paddingTop: 80, paddingBottom: 80 }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <h2 style={sectionTitle}>{t('landing.how.title')}</h2>
        <p style={sectionSubtitle}>{t('landing.how.subtitle')}</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 24,
            marginTop: 48,
          }}
        >
          {steps.map((n) => (
            <div key={n} style={{ textAlign: 'center', padding: '0 8px' }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: C.accent,
                  color: C.accentContrast,
                  display: 'grid',
                  placeItems: 'center',
                  margin: '0 auto 18px',
                  fontFamily: 'var(--font-serif)',
                  fontSize: 22,
                  fontWeight: 600,
                }}
              >
                {n}
              </div>
              <h3
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 19,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: C.ink,
                  letterSpacing: '-0.3px',
                }}
              >
                {t(`landing.how.step${n}.title`)}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: C.inkMuted }}>
                {t(`landing.how.step${n}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Social proof: stats strip + testimonials ────────────────────
const SocialProof: React.FC = () => {
  const { t } = useTranslation();
  const stats = ['noshow', 'rebook', 'minutes', 'languages'];
  const testimonials = ['1', '2', '3'];
  return (
    <section style={{ ...section, paddingTop: 80, paddingBottom: 64 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={sectionTitle}>{t('landing.social.title')}</h2>
        <p style={sectionSubtitle}>{t('landing.social.subtitle')}</p>

        {/* Stats strip */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginTop: 48,
            marginBottom: 56,
          }}
        >
          {stats.map((k) => (
            <div
              key={k}
              style={{
                textAlign: 'center',
                padding: '24px 16px',
                background: C.surface,
                border: `1px solid ${C.line}`,
                borderRadius: 16,
                boxShadow: C.shadowSm,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  fontWeight: 600,
                  color: C.accent,
                  letterSpacing: '-0.8px',
                }}
              >
                {t(`landing.social.stat.${k}.value`)}
              </div>
              <div style={{ fontSize: 13.5, color: C.inkMuted, marginTop: 6, lineHeight: 1.4 }}>
                {t(`landing.social.stat.${k}.label`)}
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {testimonials.map((n) => (
            <figure
              key={n}
              style={{
                margin: 0,
                background: C.surface,
                border: `1px solid ${C.line}`,
                borderRadius: 16,
                padding: '28px 26px',
                boxShadow: C.shadowSm,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Quote size={22} style={{ color: C.sand, marginBottom: 12 }} />
              <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={14} style={{ color: C.accent, fill: C.accent }} />
                ))}
              </div>
              <blockquote
                style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: C.ink,
                  fontStyle: 'italic',
                  flex: 1,
                }}
              >
                “{t(`landing.social.quote${n}.text`)}”
              </blockquote>
              <figcaption style={{ marginTop: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                  {t(`landing.social.quote${n}.author`)}
                </div>
                <div style={{ fontSize: 13, color: C.inkFaint }}>
                  {t(`landing.social.quote${n}.role`)}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Financial teaser: coming next ───────────────────────────────
const FinancialTeaser: React.FC = () => {
  const { t } = useTranslation();
  const items = ['1', '2', '3'];
  return (
    <section style={{ ...section, paddingTop: 56, paddingBottom: 56 }}>
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 40,
          alignItems: 'center',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 20,
          padding: 'clamp(28px, 5vw, 48px)',
          boxShadow: C.shadowSm,
        }}
      >
        <div>
          <span style={{ ...tag, background: C.bgAlt, color: C.inkMuted }}>
            {t('landing.financial.badge')}
          </span>
          <h2
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(26px, 3.5vw, 36px)',
              fontWeight: 500,
              letterSpacing: '-0.7px',
              margin: '16px 0 12px',
              color: C.ink,
            }}
          >
            {t('landing.financial.title')}
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: C.inkMuted }}>
            {t('landing.financial.subtitle')}
          </p>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {items.map((n) => (
            <li key={n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ ...featureIcon, width: 38, height: 38, flexShrink: 0 }}>
                <Wallet size={18} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 2 }}>
                  {t(`landing.financial.item${n}.title`)}
                </div>
                <div style={{ fontSize: 14, color: C.inkMuted, lineHeight: 1.5 }}>
                  {t(`landing.financial.item${n}.description`)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// ── FAQ accordion ───────────────────────────────────────────────
const Faq: React.FC = () => {
  const { t } = useTranslation();
  const items = ['1', '2', '3', '4', '5', '6'];
  const [open, setOpen] = useState<string | null>('1');
  return (
    <section style={{ ...section, background: C.bgAlt, paddingTop: 80, paddingBottom: 80 }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h2 style={sectionTitle}>{t('landing.faq.title')}</h2>
        <p style={sectionSubtitle}>{t('landing.faq.subtitle')}</p>
        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((n) => {
            const isOpen = open === n;
            return (
              <div
                key={n}
                style={{
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : n)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '18px 22px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    fontSize: 15.5,
                    fontWeight: 600,
                    color: C.ink,
                  }}
                >
                  {t(`landing.faq.q${n}.question`)}
                  <ChevronDown
                    size={18}
                    style={{
                      flexShrink: 0,
                      color: C.inkMuted,
                      transition: 'transform 0.2s',
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </button>
                {isOpen && (
                  <p
                    style={{
                      margin: 0,
                      padding: '0 22px 20px',
                      fontSize: 14.5,
                      lineHeight: 1.6,
                      color: C.inkMuted,
                    }}
                  >
                    {t(`landing.faq.q${n}.answer`)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

interface PricingTierProps {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  ctaLabel: string;
  onCta: () => void;
  highlighted?: boolean;
  badge?: string;
  note?: string;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  period,
  description,
  features,
  ctaLabel,
  onCta,
  highlighted,
  badge,
  note,
}) => (
  <div
    style={{
      background: C.surface,
      border: highlighted ? `2px solid ${C.accent}` : `1px solid ${C.line}`,
      borderRadius: 16,
      padding: '32px 28px',
      boxShadow: highlighted ? C.shadow : C.shadowSm,
      position: 'relative',
    }}
  >
    {highlighted && badge && (
      <span
        style={{
          position: 'absolute',
          top: -12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: C.accent,
          color: C.accentContrast,
          fontSize: 11,
          fontWeight: 700,
          padding: '5px 12px',
          borderRadius: 999,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {badge}
      </span>
    )}
    <h3
      style={{
        fontFamily: 'var(--font-serif)',
        fontSize: 22,
        fontWeight: 600,
        marginBottom: 6,
        color: C.ink,
        letterSpacing: '-0.3px',
      }}
    >
      {name}
    </h3>
    <p style={{ color: C.inkMuted, fontSize: 13.5, marginBottom: 20, lineHeight: 1.5 }}>{description}</p>
    <div style={{ marginBottom: 24, display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 38,
          fontWeight: 600,
          color: C.ink,
          letterSpacing: '-0.8px',
        }}
      >
        {price}
      </span>
      <span style={{ color: C.inkFaint, fontSize: 13.5 }}>{period}</span>
    </div>
    {note && (
      <p style={{ fontSize: 12, color: C.inkFaint, margin: '-16px 0 20px', lineHeight: 1.4 }}>{note}</p>
    )}
    <button
      onClick={onCta}
      style={{
        width: '100%',
        padding: '12px 14px',
        background: highlighted ? C.accent : C.surface,
        color: highlighted ? C.accentContrast : C.ink,
        border: highlighted ? 'none' : `1px solid ${C.line}`,
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        marginBottom: 24,
      }}
    >
      {ctaLabel}
    </button>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {features.map((f, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14, color: C.inkMuted, lineHeight: 1.45 }}>
          <Check size={16} style={{ flexShrink: 0, marginTop: 2, color: C.accent }} />
          {f}
        </li>
      ))}
    </ul>
  </div>
);

// ── Inline style tokens ─────────────────────────────────────────
const navInner: React.CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
};

const section: React.CSSProperties = {
  padding: '40px 24px',
};

const sectionTitle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontSize: 'clamp(28px, 4vw, 42px)',
  fontWeight: 500,
  letterSpacing: '-0.9px',
  textAlign: 'center',
  color: C.ink,
  marginBottom: 12,
};

const sectionSubtitle: React.CSSProperties = {
  textAlign: 'center',
  color: C.inkMuted,
  fontSize: 16.5,
  maxWidth: 600,
  margin: '0 auto',
  lineHeight: 1.55,
};

const tag: React.CSSProperties = {
  display: 'inline-block',
  padding: '6px 14px',
  background: C.accentSoft,
  color: C.accent,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  borderRadius: 999,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 18px',
  background: C.accent,
  color: C.accentContrast,
  border: 'none',
  borderRadius: 10,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  minHeight: 'auto',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(45, 110, 88, 0.15)',
  transition: 'transform 0.1s, box-shadow 0.1s',
};

const btnGhost: React.CSSProperties = {
  padding: '10px 16px',
  background: 'transparent',
  color: C.ink,
  border: `1px solid ${C.line}`,
  borderRadius: 10,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  minHeight: 'auto',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const featureCard: React.CSSProperties = {
  background: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: 16,
  padding: '28px 26px',
  boxShadow: C.shadowSm,
};

const featureIcon: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  background: C.accentSoft,
  color: C.accent,
  display: 'grid',
  placeItems: 'center',
};

export default Landing;
